import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Copy, Trash2, Save, Loader2, X, ChevronRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  TEMPLATE_FIELDS, CATEGORY_DEFAULT_FIELDS, TEMPLATE_DEFAULT_FORMULA,
  validateFormula, computeFromFormula, fieldLabel,
} from "@/lib/pqTemplate";
import { useLanguage } from "@/context/LanguageContext";

const BLANK_TEMPLATE = {
  id: null,
  name: "",
  remark: "",
  enabled: true,
  categories: [],
  formula: TEMPLATE_DEFAULT_FORMULA,
  fields: Object.fromEntries(TEMPLATE_FIELDS.map((k) => [k, { visible: true, default: "" }])),
};

export default function PqTemplates() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const canEdit = user?.role === "admin" || user?.role === "procurement_manager";

  const [tab, setTab] = useState("templates");  // templates | category-defaults
  const [meta, setMeta] = useState({ fields: TEMPLATE_FIELDS, category_default_fields: CATEGORY_DEFAULT_FIELDS, default_formula: TEMPLATE_DEFAULT_FORMULA, categories: [] });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [defaults, setDefaults] = useState([]);

  const refreshTemplates = async () => {
    try {
      const { data } = await api.get("/pq-templates", { params: { include_disabled: true } });
      setTemplates(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "加载模版失败");
    }
  };
  const refreshDefaults = async () => {
    try {
      const { data } = await api.get("/pq-category-defaults");
      setDefaults(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "加载种类默认值失败");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [m] = await Promise.all([
          api.get("/pq-templates/meta"),
          refreshTemplates(),
          refreshDefaults(),
        ]);
        setMeta(m.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = () => {
    setSelected({ ...BLANK_TEMPLATE, formula: meta.default_formula || TEMPLATE_DEFAULT_FORMULA });
  };

  const handlePick = (tpl) => {
    const normalised = {
      ...BLANK_TEMPLATE,
      ...tpl,
      fields: Object.fromEntries(TEMPLATE_FIELDS.map((k) => [k, tpl.fields?.[k] || { visible: true, default: "" }])),
      categories: tpl.categories || [],
    };
    setSelected(normalised);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6" data-testid="pq-templates-page">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/procurement-quotes" className="text-sm text-muted-foreground hover:text-brand flex items-center gap-1" data-testid="back-to-procurement">
          <ArrowLeft size={14} /> 返回采购报价
        </Link>
      </div>
      <h1 className="font-heading text-3xl mb-1">采购核价模版</h1>
      <p className="text-sm text-muted-foreground mb-6">
        管理员维护模版 + 种类默认值。采购员建报价时挑模版 → 按三层叠加（模版默认 → 种类默认覆盖 → 用户手改覆盖）自动填满新报价。
      </p>

      <div className="flex items-center gap-2 border-b border-border mb-4">
        {[
          { k: "templates", label: "模版库" },
          { k: "category-defaults", label: "产品种类默认值" },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setTab(s.k)}
            data-testid={`pq-tpl-tab-${s.k}`}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === s.k ? "border-brand text-brand font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {tab === "templates" ? (
        <div className="grid md:grid-cols-[1fr_2fr] gap-6">
          <div className="space-y-3">
            {canEdit && (
              <Button onClick={handleCreate} className="w-full rounded-md bg-brand hover:bg-brand-hover" data-testid="pq-tpl-new">
                <Plus size={14} /> 新建模版
              </Button>
            )}
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground p-4"><Loader2 size={14} className="animate-spin" /> 加载中…</div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4">暂无模版。管理员可创建第一个「被子模版」。</p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border bg-background">
                {templates.map((tpl) => (
                  <li key={tpl.id} data-testid={`pq-tpl-row-${tpl.id}`}
                      className={`cursor-pointer px-3 py-2.5 hover:bg-secondary/40 transition-colors ${
                        selected?.id === tpl.id ? "bg-brand/5" : ""
                      }`}
                      onClick={() => handlePick(tpl)}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{tpl.name || "(未命名)"}</span>
                      <div className="flex items-center gap-1">
                        {!tpl.enabled && <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">停用</span>}
                        <ChevronRight size={14} className="text-muted-foreground" />
                      </div>
                    </div>
                    {tpl.categories?.length > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-0.5 truncate">适用：{tpl.categories.join("、")}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            {selected ? (
              <TemplateEditor
                key={selected.id || "new"}
                template={selected}
                meta={meta}
                canEdit={canEdit}
                onSaved={async (saved) => { await refreshTemplates(); setSelected(saved); }}
                onDeleted={async () => { await refreshTemplates(); setSelected(null); }}
                onCancel={() => setSelected(null)}
              />
            ) : (
              <div className="rounded-md border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                选择一个模版，或点「新建模版」。
              </div>
            )}
          </div>
        </div>
      ) : (
        <CategoryDefaultsTable
          t={t}
          meta={meta}
          defaults={defaults}
          canEdit={canEdit}
          onChanged={refreshDefaults}
        />
      )}
    </div>
  );
}

// ─── Template editor (right panel) ─────────────────────────────────────────
function TemplateEditor({ template, meta, canEdit, onSaved, onDeleted, onCancel }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(template);
  const [saving, setSaving] = useState(false);
  const [formulaErr, setFormulaErr] = useState("");

  useEffect(() => { setForm(template); setFormulaErr(""); }, [template]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFieldSetting = (fieldKey, patch) =>
    setForm((f) => ({ ...f, fields: { ...f.fields, [fieldKey]: { ...(f.fields[fieldKey] || {}), ...patch } } }));
  const toggleCategory = (cat) =>
    setForm((f) => ({ ...f, categories: f.categories.includes(cat)
      ? f.categories.filter((c) => c !== cat)
      : [...f.categories, cat] }));

  const sample = useMemo(() => {
    try {
      validateFormula(form.formula, TEMPLATE_FIELDS);
      setFormulaErr("");
      const values = Object.fromEntries(TEMPLATE_FIELDS.map((k) => [k, form.fields?.[k]?.default]));
      return computeFromFormula(form.formula, values);
    } catch (e) {
      setFormulaErr(e.message);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formula, form.fields]);

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error("请填写模版名称"); return; }
    if (formulaErr) { toast.error(formulaErr); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        remark: form.remark || "",
        enabled: !!form.enabled,
        categories: form.categories || [],
        fields: form.fields,
        formula: form.formula,
      };
      const { data } = form.id
        ? await api.put(`/pq-templates/${form.id}`, payload)
        : await api.post(`/pq-templates`, payload);
      // 2026-10-07 · toast id dedupes double-fires from React 18 strict mode.
      toast.success(form.id ? "模版已更新" : "模版已创建", { id: "pq-tpl-save" });
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!form.id) return;
    try {
      const { data } = await api.post(`/pq-templates/${form.id}/duplicate`);
      toast.success("已复制为新模版");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "复制失败");
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`确认删除模版「${form.name}」？`)) return;
    try {
      await api.delete(`/pq-templates/${form.id}`);
      toast.success("模版已删除");
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "删除失败");
    }
  };

  const insertField = (k) => {
    setField("formula", (form.formula || "") + (form.formula?.trim() ? " + " : "") + k);
  };

  return (
    <div className="rounded-md border border-border bg-background p-4 space-y-4" data-testid="pq-tpl-editor">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading text-lg">{form.id ? "编辑模版" : "新建模版"}</h2>
        {canEdit && (
          <div className="flex items-center gap-2">
            {form.id && <Button variant="outline" size="sm" onClick={handleDuplicate} className="h-8 rounded-md" data-testid="pq-tpl-duplicate"><Copy size={14} /> 复制</Button>}
            {form.id && <Button variant="outline" size="sm" onClick={handleDelete} className="h-8 rounded-md text-red-600 hover:text-red-700" data-testid="pq-tpl-delete"><Trash2 size={14} /> 删除</Button>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] text-muted-foreground">模版名称 *</Label>
          <Input value={form.name || ""} onChange={(e) => setField("name", e.target.value)} disabled={!canEdit}
            className="h-8" data-testid="pq-tpl-name" />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">适用种类（可多选）</Label>
          <div className="flex flex-wrap gap-1.5 pt-1 min-h-8">
            {(meta.categories || []).length === 0 ? (
              <span className="text-xs text-muted-foreground italic">设置中暂无种类</span>
            ) : (
              meta.categories.map((cat) => (
                <button key={cat} type="button" onClick={() => canEdit && toggleCategory(cat)}
                  data-testid={`pq-tpl-cat-${cat}`}
                  disabled={!canEdit}
                  className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                    form.categories.includes(cat)
                      ? "bg-brand text-white border-brand"
                      : "bg-background text-muted-foreground border-border hover:border-brand"
                  }`}>
                  {cat}
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div>
        <Label className="text-[11px] text-muted-foreground">备注</Label>
        <Textarea rows={2} value={form.remark || ""} onChange={(e) => setField("remark", e.target.value)}
          disabled={!canEdit} className="text-sm" data-testid="pq-tpl-remark" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-sm font-semibold">字段设置</h3>
          <label className="text-xs flex items-center gap-1.5 text-muted-foreground">
            <Checkbox checked={!!form.enabled} onCheckedChange={(v) => setField("enabled", !!v)} disabled={!canEdit} data-testid="pq-tpl-enabled" /> 启用
          </label>
        </div>
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-left px-2 py-1.5 w-28">字段</th>
                <th className="w-20 text-center px-2 py-1.5">显示</th>
                <th className="text-left px-2 py-1.5">默认值</th>
              </tr>
            </thead>
            <tbody>
              {TEMPLATE_FIELDS.map((k) => {
                const cfg = form.fields?.[k] || { visible: true, default: "" };
                return (
                  <tr key={k} className="border-t border-border">
                    <td className="px-2 py-1 text-xs text-muted-foreground">{fieldLabel(t, k)}</td>
                    <td className="px-2 py-1 text-center">
                      <Checkbox checked={!!cfg.visible} onCheckedChange={(v) => setFieldSetting(k, { visible: !!v })} disabled={!canEdit}
                        data-testid={`pq-tpl-field-visible-${k}`} />
                    </td>
                    <td className="px-2 py-1">
                      <Input value={cfg.default ?? ""} onChange={(e) => setFieldSetting(k, { default: e.target.value })} disabled={!canEdit}
                        className="h-7 text-xs" placeholder="0" data-testid={`pq-tpl-field-default-${k}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-sm font-semibold">价格公式</h3>
          <span className={`text-xs font-mono ${sample !== null ? "text-brand" : "text-red-500"}`} data-testid="pq-tpl-sample-price">
            样例价：{sample !== null ? sample : "—"}
          </span>
        </div>
        <Input value={form.formula || ""} onChange={(e) => setField("formula", e.target.value)} disabled={!canEdit}
          className={`font-mono text-xs ${formulaErr ? "border-red-500" : ""}`}
          placeholder={TEMPLATE_DEFAULT_FORMULA}
          data-testid="pq-tpl-formula" />
        {formulaErr && <p className="text-xs text-red-600 mt-1" data-testid="pq-tpl-formula-error">{formulaErr}</p>}
        <div className="flex flex-wrap gap-1 mt-2">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">可用字段:</span>
          {TEMPLATE_FIELDS.map((k) => (
            <button key={k} type="button" onClick={() => canEdit && insertField(k)}
              className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary/60 hover:bg-brand/10 hover:text-brand transition-colors"
              disabled={!canEdit}
              data-testid={`pq-tpl-insert-${k}`}>
              {k}
            </button>
          ))}
        </div>
      </div>

      {canEdit && (
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <Button variant="outline" onClick={onCancel} className="rounded-md" data-testid="pq-tpl-cancel">取消</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-md bg-brand hover:bg-brand-hover" data-testid="pq-tpl-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} 保存
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Category defaults table (tab 2) ───────────────────────────────────────
function CategoryDefaultsTable({ t, meta, defaults, canEdit, onChanged }) {
  const [editing, setEditing] = useState({});  // cat -> { field: value } overrides
  const [saving, setSaving] = useState("");

  const row = (cat) => {
    const base = defaults.find((d) => d.category === cat)?.defaults || {};
    return { ...base, ...(editing[cat] || {}) };
  };

  const setCell = (cat, k, v) => setEditing((e) => ({ ...e, [cat]: { ...(e[cat] || {}), [k]: v } }));

  const save = async (cat) => {
    const current = row(cat);
    setSaving(cat);
    try {
      await api.put(`/pq-category-defaults/${encodeURIComponent(cat)}`, { category: cat, defaults: current });
      toast.success(`${cat} 默认值已保存`);
      setEditing((e) => { const n = { ...e }; delete n[cat]; return n; });
      await onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "保存失败");
    } finally {
      setSaving("");
    }
  };

  if (!meta.categories?.length) {
    return <p className="text-sm text-muted-foreground p-4">请先到「设置」里配置产品种类，再回来维护默认值。</p>;
  }

  return (
    <div className="rounded-md border border-border overflow-x-auto" data-testid="pq-cat-defaults-table">
      <table className="w-full text-sm">
        <thead className="bg-secondary/40">
          <tr>
            <th className="text-left px-3 py-2 w-40">种类</th>
            {CATEGORY_DEFAULT_FIELDS.map((k) => (
              <th key={k} className="text-left px-3 py-2">{fieldLabel(t, k)}</th>
            ))}
            <th className="text-right px-3 py-2 w-28">操作</th>
          </tr>
        </thead>
        <tbody>
          {meta.categories.map((cat) => {
            const r = row(cat);
            const dirty = !!editing[cat];
            return (
              <tr key={cat} className="border-t border-border" data-testid={`pq-cat-row-${cat}`}>
                <td className="px-3 py-1.5 font-medium">{cat}</td>
                {CATEGORY_DEFAULT_FIELDS.map((k) => (
                  <td key={k} className="px-2 py-1">
                    <Input value={r[k] ?? ""} onChange={(e) => setCell(cat, k, e.target.value)}
                      disabled={!canEdit} className="h-7 text-xs" placeholder="0"
                      data-testid={`pq-cat-${cat}-${k}`} />
                  </td>
                ))}
                <td className="px-2 py-1 text-right">
                  {canEdit && (
                    <Button size="sm" variant={dirty ? "default" : "outline"} className="h-7 rounded-md"
                      onClick={() => save(cat)} disabled={!dirty || saving === cat}
                      data-testid={`pq-cat-save-${cat}`}>
                      {saving === cat ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} 保存
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
